import type {BridgeComponent, BridgeComponentConstructor} from '../types';

type AngularDeps = {
  Component: any;
  NgModule: any;
  ElementRef: any;
  NgZone: any;
  Inject: any;
};

/**
 * Creates an Angular driver for a bridge component.
 * Returns an Angular module that declares a component which:
 * - On ngOnInit: creates bridge instance, renders into host element
 * - On ngDoCheck: calls updateProps when props change
 * - On ngOnDestroy: calls destroy
 */
export function createAngularDriver<P extends Record<string, any>>(
  ComponentClass: BridgeComponentConstructor<P>
): (deps: AngularDeps) => any {
  return ({Component: AngularComponent, NgModule, ElementRef, NgZone, Inject}: AngularDeps) => {
    class ComponentInstance {
      elementRef: any;
      zone: any;
      props: Record<string, any> = {};
      __bridgeInstance: BridgeComponent<P> | null = null;
      private _prevProps: Record<string, any> = {};

      static parameters: any[];
      static annotations: any[];

      constructor(elementRef: any, zone: any) {
        this.elementRef = elementRef;
        this.zone = zone;
      }

      getProps(): P {
        const wrapped: Record<string, any> = {...this.props};
        const {zone} = this;
        // Wrap function props to run inside Angular zone
        for (const [key, value] of Object.entries(wrapped)) {
          if (typeof value === 'function') {
            wrapped[key] = function angularZoneWrapped(this: any, ...args: any[]) {
              return zone.run(() => value.apply(this, args));
            };
          }
        }
        return wrapped as P;
      }

      ngOnInit() {
        const el = this.elementRef.nativeElement;
        this.__bridgeInstance = new ComponentClass(this.getProps());
        this.__bridgeInstance.render(el).catch(console.error);
      }

      ngDoCheck() {
        if (this.__bridgeInstance && !shallowEqual(this._prevProps, this.props)) {
          this._prevProps = {...this.props};
          this.__bridgeInstance.updateProps(this.getProps()).catch(() => {});
        }
      }

      ngOnDestroy() {
        if (this.__bridgeInstance) {
          this.__bridgeInstance.destroy();
          this.__bridgeInstance = null;
        }
      }
    }

    ComponentInstance.parameters = [[new Inject(ElementRef)], [new Inject(NgZone)]];

    ComponentInstance.annotations = [
      new AngularComponent({
        selector: (ComponentClass.prototype as any).__options?.tag || 'bridge-component',
        template: '<div></div>',
        inputs: ['props']
      })
    ];

    class ModuleInstance {
      static annotations: any[];
    }

    ModuleInstance.annotations = [
      new NgModule({
        declarations: [ComponentInstance],
        exports: [ComponentInstance]
      })
    ];

    return ModuleInstance;
  };
}

function shallowEqual(a: Record<string, any>, b: Record<string, any>): boolean {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}
